import cn from "classnames";
import * as Eden from "src/eden/content";
import { HitterTier, MIN_RATED_AB, formatRate, hitterTier } from "./hitter-rating";
import Styles from "./HitterChip.module.scss";

interface Props {
  season: Eden.OpponentSeasonBatter;
}

/**
 * Compact opponent scouting cue shown under a batter's name in the Box Score: a colored AVG pill
 * (heat scale — see {@link hitterTier}) plus the batter's season OPS and HR. The full slash line is
 * exposed via title/aria-label so the cue isn't color-only. Mobile-first: wraps under the name.
 */
export function HitterChip({ season }: Props) {
  const tier = hitterTier(season.avg, season.ab);
  const lowSample = season.ab < MIN_RATED_AB;
  const label =
    `Season: ${formatRate(season.avg)} AVG · ${formatRate(season.ops)} OPS · ${season.hr} HR · ` +
    `${season.ab} AB` +
    (lowSample ? " (small sample)" : "");

  return (
    <span className={Styles.chipRow} title={label} aria-label={label}>
      <span className={cn(Styles.avg, Styles[tier])}>{formatRate(season.avg)}</span>
      <span className={Styles.meta}>
        OPS {formatRate(season.ops)} · {season.hr} HR
      </span>
    </span>
  );
}

const LEGEND: Array<{ tier: HitterTier; label: string }> = [
  { tier: "cold", label: ".000" },
  { tier: "green", label: ".100" },
  { tier: "yellow", label: ".200" },
  { tier: "orange", label: ".300" },
  { tier: "red", label: ".400+" },
];

/** Tiny key explaining the opponent AVG heat scale. Shown once under a box score that has cues. */
export function HitterLegend() {
  return (
    <div className={Styles.legend}>
      <span className={Styles.legendLabel}>Opponent season AVG</span>
      {LEGEND.map(({ tier, label }) => (
        <span key={tier} className={Styles.legendItem}>
          <span className={cn(Styles.swatch, Styles[tier])} aria-hidden="true" />
          {label}
        </span>
      ))}
    </div>
  );
}
